import React, { useState } from 'react';
import {
  Loader2, AlertTriangle, Lock, CircleSlash, ChevronRight, Check,
  Search, Wrench, Database, Play, MessageSquare, ShieldCheck, FileText,
  Globe, Coins, Terminal, Layers, Download,
} from 'lucide-react';
import { exportAnswer } from '../../utils/insightsExcel';

// =============================================================================
// The conversation thread.
//
// MODELLED ON HOW A CODING AGENT REPORTS ITSELF, because the problem is the same
// one: a machine did several things on your behalf and you need to judge whether
// to trust the result. The answer to that is never the answer alone — it is
// seeing what it looked at, what it ran, and what came back.
//
// So an assistant turn renders in three layers:
//
//   1. STEPS — collapsed to one line each while running, the way a tool call
//      does. What it read, what it planned, what it ran, whether figures left.
//   2. THE FIGURES — the table. This is the answer.
//   3. THE PROSE — the model's reading of the table, last.
//
// THE ORDER IS DELIBERATE AND IT IS THE OPPOSITE OF A CHATBOT. Put the paragraph
// first and people stop looking at the numbers, which is the exact moment a
// plausible wrong answer does damage. The prose is commentary on evidence that is
// already on screen.
// =============================================================================

const STEP_ICONS = {
  thinking: Search,
  chat: Globe,
  sql: Terminal,
  plan: Wrench,
  compile: Database,
  query: Play,
  gate: Lock,
  answer: MessageSquare,
  error: AlertTriangle,
};

// One step, collapsed. Expands only where there is something worth reading —
// the SQL, the scope filters. A row that expands to nothing is a row that
// teaches people not to click.
const Step = ({ step, running }) => {
  const [open, setOpen] = useState(false);
  const Icon = STEP_ICONS[step.kind] || Check;
  const expandable = !!(step.sql || step.scopeApplied?.length);
  const isGateOut = step.kind === 'gate' && /^Sent /.test(step.label || '');

  return (
    <div className="text-[11px]">
      <button
        type="button"
        disabled={!expandable}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-start gap-2 py-1 text-left ${expandable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
      >
        <span className={`shrink-0 mt-0.5 ${step.kind === 'error'
          ? 'text-red-500'
          : isGateOut
            ? 'text-amber-500'
            : step.kind === 'gate'
              ? 'text-emerald-500'
              : 'text-slate-400'}`}>
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-medium text-slate-600 dark:text-slate-300">{step.label}</span>
          {step.detail && <span className="text-slate-400"> · {step.detail}</span>}
          {step.corrected && (
            <span className="ml-1.5 px-1 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 text-[10px]">
              corrected once
            </span>
          )}
        </span>
        {expandable && (
          <ChevronRight className={`w-3 h-3 shrink-0 mt-0.5 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
        )}
      </button>

      {open && (
        <div className="ml-5 mb-2 space-y-2">
          {step.scopeApplied?.length > 0 && (
            <div>
              <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Applied by Mantle, not the model
              </p>
              <ul className="m-0 mt-0.5 pl-4 list-disc text-slate-500 dark:text-slate-400">
                {step.scopeApplied.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </div>
          )}
          {step.sql && (
            <pre className="m-0 p-2.5 rounded-lg bg-slate-900 text-slate-100 text-[10px] leading-relaxed overflow-x-auto whitespace-pre-wrap">
              {step.sql}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// Right-align numerics so a column of figures can be scanned, and render NULL as
// an em dash. "No value" and "zero" are different answers, and the whole
// catalogue is built around not confusing them.
const Cell = ({ value }) => {
  const isNull = value === null || value === undefined || value === '';
  const asNum = !isNull && typeof value !== 'boolean' && value !== '' && !Number.isNaN(Number(value))
    ? Number(value) : null;
  return (
    <td className={`px-3 py-1.5 whitespace-nowrap ${asNum !== null ? 'text-right tabular-nums' : ''} ${isNull ? 'text-slate-300 dark:text-slate-600' : 'text-slate-700 dark:text-slate-200'}`}>
      {isNull ? '—' : asNum !== null ? asNum.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value)}
    </td>
  );
};

const ResultTable = ({ columns = [], rows = [], truncated, rowCount }) => {
  if (!columns.length) return null;
  if (!rows.length) {
    return (
      <div className="mt-2 px-4 py-4 rounded-lg border border-slate-200 dark:border-slate-700 text-center">
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">The query returned no rows.</p>
        <p className="m-0 mt-0.5 text-[11px] text-slate-400">
          That is an answer, not an error — but check the version, site and period the question implied.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* overflow-AUTO, not overflow-x-auto: the sticky header below needs this
          div to be its scroll container. With only the x axis scrollable the
          header resolves against the page instead and floats over the turn
          above it as the thread scrolls. */}
      <div className="overflow-auto max-h-[420px]">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 dark:bg-slate-900">
              {columns.map((c) => (
                <th key={c} className="text-left px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap border-b border-slate-200 dark:border-slate-700">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-slate-700/60 last:border-0">
                {r.map((v, j) => <Cell key={j} value={v} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && (
        <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-500/10 border-t border-amber-200 dark:border-amber-500/30">
          <p className="m-0 text-[10px] text-amber-800 dark:text-amber-300">
            First {rowCount} rows only — any total covers just what is shown.
          </p>
        </div>
      )}
    </div>
  );
};

// The seam. Renders nothing today because nothing produces artifacts yet; when
// the model may generate a spreadsheet or a PDF, a turn carries them here and
// this is already where they appear.
const Artifacts = ({ artifacts = [] }) => {
  if (!artifacts.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {artifacts.map((a) => (
        <a key={a.url} href={a.url} download
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
          <FileText className="w-3.5 h-3.5" />
          {a.name}
          {a.bytes && <span className="text-slate-400">{Math.ceil(a.bytes / 1024)}kB</span>}
        </a>
      ))}
    </div>
  );
};

// What the turn cost. Shown because it is the tenant's own API key being spent,
// and Keith asked for it the moment he saw the numbers were already being
// measured: "I see the tokens been used is returned please display to the user."
//
// Money appears only when the model is priced. A self-hosted model has no rate,
// and showing R0.00 there would read as "free" rather than "not priced".
const Usage = ({ usage }) => {
  if (!usage?.total_tokens) return null;
  const money = usage.cost != null
    ? `${usage.currency === 'ZAR' ? 'R' : usage.currency === 'USD' ? '$' : `${usage.currency} `}${usage.cost.toFixed(4)}`
    : null;

  // HOW MUCH OF THE PROMPT WAS ALREADY PAID FOR. The schema briefing is the bulk
  // of every question and is identical each time, so from the second question in
  // a conversation it comes back from the provider's cache at about a tenth of
  // the price. The token count does NOT fall — the briefing was still sent and
  // still read — only the money does, so showing the saving as a smaller number
  // of tokens would be a lie. It is shown as what it is: how much was cached.
  const cached = usage.cached_tokens || 0;
  const title = [
    `${usage.input_tokens.toLocaleString()} in + ${usage.output_tokens.toLocaleString()} out`
      + ` across ${usage.calls} call${usage.calls === 1 ? '' : 's'}`,
    cached ? `${cached.toLocaleString()} of the input came from the provider's cache, at about a tenth of the price` : null,
    usage.cache_write_tokens ? `${usage.cache_write_tokens.toLocaleString()} written to cache for the next question` : null,
  ].filter(Boolean).join('\n');

  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-400" title={title}>
      <Coins className="w-3 h-3" />
      {usage.total_tokens.toLocaleString()} tokens
      {cached > 0 && (
        <span className="text-emerald-600 dark:text-emerald-400">
          · {Math.round((cached / usage.input_tokens) * 100)}% cached
        </span>
      )}
      {money && <> · {money}</>}
    </span>
  );
};

// Take the table away with you.
//
// Built entirely from what is already on screen — no second query, no second
// model call. The answer in the file is the answer that was shown, which is the
// only arrangement in which the two cannot disagree.
//
// Everything the surrounding UI says about these figures — which version, whose
// scope, whether they were truncated, which path produced them — goes into the
// file's About sheet, because none of it survives the trip otherwise. See
// utils/insightsExcel.js.
const ExportButton = ({ question, msg, versionId, modelLabel }) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);

  const run = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await exportAnswer({ question, msg, versionId, modelLabel });
    } catch (e) {
      setFailed(e.message || 'The export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={run}
        disabled={busy}
        title="Download these figures as an Excel file, with a sheet recording what they cover"
        className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        Excel
      </button>
      {failed && <span className="text-[10px] text-red-500">{failed}</span>}
    </span>
  );
};

const UserTurn = ({ content }) => (
  <div className="flex justify-end mb-5">
    <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-sm bg-slate-900 dark:bg-slate-700 text-white text-sm whitespace-pre-wrap">
      {content}
    </div>
  </div>
);

const AssistantTurn = ({ msg, running, question, versionId, modelLabel }) => {
  const [showSteps, setShowSteps] = useState(false);
  const steps = msg.steps || [];
  const sentOut = !!msg.results_sent;

  return (
    <div className="mb-7">
      {/* ---- STEPS ---- */}
      {steps.length > 0 && (
        <div className="mb-2">
          {/* While it is working, every step is visible — that IS the progress
              indicator. Once finished they collapse to one line, because the
              answer is what somebody came for. */}
          {running || showSteps ? (
            <div className="pl-1 border-l-2 border-slate-200 dark:border-slate-700 ml-1">
              <div className="pl-3">
                {steps.map((s, i) => (
                  <Step key={i} step={s} running={running && i === steps.length - 1} />
                ))}
              </div>
              {!running && (
                <button type="button" onClick={() => setShowSteps(false)}
                  className="ml-4 mt-1 text-[10px] text-slate-400 hover:underline">
                  hide
                </button>
              )}
            </div>
          ) : (
            <button type="button" onClick={() => setShowSteps(true)}
              className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              <ChevronRight className="w-3 h-3" />
              {steps.length} steps
              {steps.find((s) => s.kind === 'query')?.detail && ` · ${steps.find((s) => s.kind === 'query').detail}`}
            </button>
          )}
        </div>
      )}

      {running && steps.length === 0 && (
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" /> Working…
        </div>
      )}

      {/* ---- ANSWERED WITHOUT TOUCHING THE COMPANY'S DATA ----
           Labelled, deliberately. The same paragraph rendered plainly inside a
           budgeting system reads as though it came out of the budget. */}
      {msg.from_general_knowledge && (
        <div className="mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700/60 text-[10px] text-slate-500 dark:text-slate-400">
          <Globe className="w-3 h-3" />
          Answered from the model's general knowledge — not from your data
        </div>
      )}

      {/* ---- REFUSAL ---- */}
      {msg.outcome === 'refused' && !msg.from_general_knowledge && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
          <CircleSlash className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
          <div>
            <p className="m-0 text-sm text-slate-700 dark:text-slate-200">{msg.content}</p>
            <p className="m-0 mt-1 text-[11px] text-slate-400">
              No query was run. If the data does exist, it may need adding to the catalogue.
            </p>
          </div>
        </div>
      )}

      {/* ---- REPLAY FAILURE (reopened conversation) ---- */}
      {msg.replay_error && (
        <div className="mb-2 flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <p className="m-0 text-[11px] text-amber-800 dark:text-amber-300">
            The figures for this answer could not be re-run: {msg.replay_error}
          </p>
        </div>
      )}

      {/* ---- THE FIGURES, BEFORE THE PROSE ---- */}
      {msg.columns?.length > 0 && (
        <>
          {msg.intent && (
            <p className="m-0 mb-1 text-[11px] text-slate-400">{msg.intent}</p>
          )}
          <ResultTable
            columns={msg.columns}
            rows={msg.rows}
            truncated={msg.truncated}
            rowCount={msg.row_count}
          />
        </>
      )}

      <Artifacts artifacts={msg.artifacts} />

      {/* ---- THE PROSE ---- */}
      {msg.outcome !== 'refused' && msg.content && (
        <div className="mt-3">
          {String(msg.content).split(/\n\s*\n/).map((para, i) => (
            <p key={i} className="m-0 mb-2 last:mb-0 text-sm leading-relaxed text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
              {para}
            </p>
          ))}
        </div>
      )}

      {/* ---- PROVENANCE AND COST ---- */}
      {(msg.columns?.length > 0 || msg.usage) && (
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          {msg.columns?.length > 0 && (
            <span className={`inline-flex items-center gap-1 text-[10px] ${sentOut
              ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {sentOut ? <AlertTriangle className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
              {sentOut ? 'These figures were sent to the AI service' : 'These figures stayed in Mantle'}
            </span>
          )}

          {/* WHICH PATH ANSWERED. Not decoration: on the SQL path Mantle did NOT
              inject the version, site or GL scope, and somebody reading the
              figures should know which kind of answer they are looking at. */}
          {msg.query?.mode === 'sql' && (
            <span className="inline-flex items-center gap-1 text-[10px] text-violet-600 dark:text-violet-400"
                  title="The model wrote this SQL. Mantle did not add the version, site or GL filters it adds on the standard path.">
              <Terminal className="w-3 h-3" />
              Direct SQL
            </span>
          )}

          {/* THE HONEST DISCLOSURE on the SQL path: what the answer actually
              covers, since Mantle could not force it. */}
          {msg.query?.version_span && (
            msg.query.version_span.known ? (
              <span className={`inline-flex items-center gap-1 text-[10px] ${msg.query.version_span.versions.length > 1
                ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>
                <Layers className="w-3 h-3" />
                {msg.query.version_span.versions.length > 1
                  ? `Covers ${msg.query.version_span.versions.length} versions: ${msg.query.version_span.versions.join(', ')}`
                  : `Version ${msg.query.version_span.versions[0] || '—'}`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400"
                    title="No version column was returned, so Mantle cannot confirm which budget versions these figures span.">
                <Layers className="w-3 h-3" />
                Version coverage unknown
              </span>
            )
          )}
          <Usage usage={msg.usage} />
          {msg.columns?.length > 0 && !running && (
            <ExportButton question={question} msg={msg} versionId={versionId} modelLabel={modelLabel} />
          )}
        </div>
      )}
    </div>
  );
};

export default function Thread({ messages, running, versionId, modelLabel }) {
  return (
    <div>
      {messages.map((m, i) => (
        m.role === 'user'
          ? <UserTurn key={m.message_id || `u${i}`} content={m.content} />
          : <AssistantTurn
              key={m.message_id || `a${i}`}
              msg={m}
              running={running && i === messages.length - 1}
              // The question this turn answered. An exported file that does not
              // say what was asked is a grid of numbers with no claim attached.
              question={messages[i - 1]?.role === 'user' ? messages[i - 1].content : null}
              versionId={versionId}
              modelLabel={modelLabel}
            />
      ))}
    </div>
  );
}

export { ResultTable, Step };
